import React, { useCallback, useEffect, useState } from 'react';
import { intersection } from 'lodash';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import * as R from 'ramda';
import { AlertService } from '@services/alert';
import * as searchUtil from '@common/utils/search';
import Container from '@common/components/container';
import { Button } from '@common/components/button';
import Rows from '@common/components/rows';
import Placeholder from '@common/components/placeholder';
import Bar from '@common/components/bar';
import Spinner from '@common/components/spinner';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import CourseItem from '../../components/academy-item';
import type { StoreState } from '@common/types/store';
import * as coursesSelector from '../../selectors/courses';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import {
  changeCourseTypeAction,
  duplicateCourse as duplicateCourseAction,
} from '../../actions';
import fetchCoursesAction from '../../actions/fetch-courses';
import updateCourseAction from '../../actions/update-course';
import removeCourseAction from '../../actions/remove-course';
import updateCoursesOrderAction from '../../actions/update-courses-order';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import { combineClassNames } from '@common/utils/combineClassNames';
import { Api } from '@common/services/api';
import { useIsAvailableInPlanPackage } from '@common/hooks/use-is-available-in-plan-package';
import dndManager from '@common/utils/dnd-manager';
import {
  EPlanPackageConfig,
  EPredicateFields,
  EStatus,
} from '@common/definitions';
import { Course } from '@modules/learning/types/objects';
import { Network, Organisation, Team } from '@common/types/objects';
import { ApiResponse } from '@common/services/api/types';
import { usePredicateState } from '@common/hooks/use-predicate-state';
import AIModuleForm from '@modules/learning/forms/ai-module';

const mapStateToProps = (state: StoreState) => ({
  courses: coursesSelector.academy(state),
  networks: organisationSelector.networks(state),
  functions: organisationSelector.functions(state),
  organisation: organisationSelector.selected(state),
  organisationId: state.organisation.selected.id,
});

const mapDispatchToProps = {
  changeCourseType: changeCourseTypeAction,
  fetchCourses: fetchCoursesAction,
  updateCourse: updateCourseAction,
  removeCourse: removeCourseAction,
  updateCoursesOrder: updateCoursesOrderAction,
  duplicateCourse: duplicateCourseAction,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = {
  organisationId?: string;
  courses: Course[];
  networks: Network[];
  organisation: Organisation;
  functions: any;
  fetchCourses: any;
  duplicateCourse: any;
  removeCourse: any;
  changeCourseType: any;
  updateCoursesOrder: any;
  updateCourse: any;
};

type CoursesCountMeta = {
  counts: {
    total: number;
  }
};

const TITLE_KEYS = {
  [EStatus.DRAFT]: 'learning:academy_courses_draft',
  [EStatus.LIVE]: 'learning:academy_courses_live',
  [EStatus.ARCHIVED]: 'learning:academy_courses_archived',
};

const getOrder = (items: Course[]) => {
  return items
    .filter(({ type }) => type === 'academy')
    .map(({ id }) => id);
};

const LearningAcademyContainer = ({
  courses, networks, functions, organisationId, organisation,
  fetchCourses, duplicateCourse, removeCourse, changeCourseType, updateCoursesOrder, updateCourse,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams<{ status?: EStatus }>();
  const [isFetching, setIsFetching] = useState(true);
  const [search, setSearch] = useState('');
  const [amountOfOnboardingCourses, setAmountOfOnboardingCourses] = useState<number | undefined>(undefined);
  const [order, setOrder] = useState<string[]>(() => courses.map(({ id }) => id));
  const [networksData, setNetworksData] = useState<Network[]>([]);
  const [functionsData, setFunctionsData] = useState<Team[]>([]);

  const [predicates, setPredicates] = usePredicateState({
    networks: networksData,
    functions: functionsData,
  });

  useEffect(() => {
    const init = async () => {
      const [data, meta, newNetworks, newFunctions] = await Promise.all([
        fetchCourses(),
        Api.get<ApiResponse<{}, CoursesCountMeta>>(`/v2/organisations/${organisationId}/courses?limit=0&type=onboarding`),
        Api.get(`/v4/organisations/${organisationId}/networks?include_all=true`),
        Api.get(`/v3/organisations/${organisationId}/functions`),
      ]);
      setAmountOfOnboardingCourses(meta.meta?.counts.total);
      setOrder(getOrder(data.items));
      setNetworksData(newNetworks.data as Network[]);
      setFunctionsData(newFunctions.data as Team[]);
      setIsFetching(false);
    };
    init();
  }, [
    organisationId, fetchCourses, setOrder,
    setAmountOfOnboardingCourses, setPredicates, setIsFetching,
  ]);

  const filteredItems = getFilteredItems(courses, predicates, search || '', order, params);

  const {
    isAvailable: canCreateCourses, showUpgradeModal: showUpgradeToCreateCoursesModal,
  } = useIsAvailableInPlanPackage(EPlanPackageConfig.ACADEMY, filteredItems?.length);
  const {
    isAvailable: canMoveCoursesToOnboarding, showUpgradeModal: showUpgradeToMoveModal,
  } = useIsAvailableInPlanPackage(EPlanPackageConfig.ACADEMY_ONBOARDING, amountOfOnboardingCourses);

  const onDuplicate = useCallback(async (id) => {
    if (canCreateCourses) {
      try {
        await duplicateCourse(id);
        const data = await fetchCourses();
        setOrder(getOrder(data.items));
        AlertService.success(t('learning:duplicated_course'));
      } catch (err) {
        AlertService.warning(t('learning:warning_duplicate_course'));
      }
    } else {
      showUpgradeToCreateCoursesModal();
    }
  }, [
    canCreateCourses, getOrder, setOrder, showUpgradeToCreateCoursesModal,
    duplicateCourse, fetchCourses, t,
  ]);

  const onDelete = useCallback(async (id) => {
    await removeCourse(id);
    AlertService.success(t('learning:removed_course'));
  }, [removeCourse, t]);

  const onMoveCourseToOnboarding = useCallback(async (courseId: string) => {
    if (canMoveCoursesToOnboarding) {
      try {
        await changeCourseType(courseId);
        setAmountOfOnboardingCourses((amountOfOnboardingCourses && amountOfOnboardingCourses + 1) || 1);
        setOrder((previousOrder) => previousOrder.filter((id) => id !== courseId));
      } catch (e) {
        AlertService.error(t('learning:unable_to_move_course'));
      }
    } else {
      showUpgradeToMoveModal();
    }
  }, [
    amountOfOnboardingCourses, canMoveCoursesToOnboarding, changeCourseType,
    showUpgradeToMoveModal, setAmountOfOnboardingCourses, setOrder, t,
  ]);

  const onChangeOrder = useCallback((sourceId: string, targetId: string) => {
    const index = R.indexOf(targetId, order);
    setOrder(R.insert(index, sourceId, R.reject(R.equals(sourceId), order)));
  }, [order, setOrder]);

  const onDrop = useCallback(async () => {
    try {
      await updateCoursesOrder(order);
      AlertService.success(t('learning:saved_order'));
    } catch (response: any) {
      AlertService.forStatus(response.status_code, {
        warning: t('learning:warning_saving_order'),
        error: t('learning:error_saving_order'),
      });
    }
  }, [updateCoursesOrder, order, t]);

  const onPublish = useCallback((id: string, publish: boolean) => {
    if (publish) {
      history.push(`/admin/learning/courses/${id}/publish/academy`);
    } else {
      updateCourse(id, { published: false });

      AlertService.success(t('learning:unpublished_course'));
    }
  }, [history, updateCourse, t]);

  const onArchive = useCallback((id: string, archived: boolean) => {
    updateCourse(id, { archived }, true);
    AlertService.success(archived ? t('learning:course_archived') : t('learning:course_unarchived'));
  }, [updateCourse, t]);

  const onCreateCourse = useCallback(() => {
    if (canCreateCourses) {
      history.push('/admin/learning/academy/create');
    } else {
      showUpgradeToCreateCoursesModal();
    }
  }, [canCreateCourses, history, showUpgradeToCreateCoursesModal]);

  const handleOpenModule = useCallback(
    (type: string) => {
      if (canCreateCourses) {
        if (type === 'manual') {
          history.push('/admin/learning/academy/create');
        } else if (type === 'genai') {
          history.push('/admin/learning/academy/course/generate');
        }
      } else {
        showUpgradeToCreateCoursesModal();
      }
    },
    [canCreateCourses, history, showUpgradeToCreateCoursesModal]
  );

  return (
    <Container.Content>
      <Bar>
        <h2 className="pull-left">
          <Trans
            i18nKey={getTitleKey(params.status)}
            values={{ count: filteredItems.length }}
          />
        </h2>
        <div className="pull-right">
          <AIModuleForm onOpenCourse={handleOpenModule}>
            <Button type="primary" iconRight="add" size="large">
              <Trans i18nKey="learning:onboarding_create_course" />
            </Button>
          </AIModuleForm>
        </div>
      </Bar>
      {isFetching ? (
        <Spinner centered size="large" />
      ) : (
        <>
          <Bar>
            <FiltersToolbar
              predicates={predicates}
              setPredicates={setPredicates}
              organisation={organisation}
              debounce={false}
              onSearch={setSearch}
            />
          </Bar>
          <DndProvider manager={dndManager}>
            <Rows
              items={filteredItems}
              itemsPerRow={window.innerWidth < 1337 ? 3 : 4}
              render={CourseItem}
              rowProps={{
                onArchive,
                onOpen: (id: string) => history.push(params.status ?
                  `/admin/learning/courses/${id}?backUrl=/admin/learning/academy/filter/${params.status}` :
                  `/admin/learning/courses/${id}`
                ),
                onDelete,
                onChangeOrder,
                onPublish,
                onDrop,
                onDuplicate,
                onChangeCourseType: onMoveCourseToOnboarding,
                networks,
                functions,
                showStatus: !params.status,
              }}
              rowClassName={combineClassNames('Academy__Collections', {
                'Academy__Collections--responsive': window.innerWidth < 1337,
              })}
              placeholder={search ? (
                <Placeholder
                  title={t('learning:academy_course_search_placeholder_title')}
                  action={t('learning:academy_course_search_placeholder_action')}
                />
              ) : (
                <Placeholder
                  image="/static/images/courses-placeholder.svg"
                  title={t('learning:academy_course_placeholder_title')}
                  description={t('learning:academy_course_placeholder_description')}
                  action={(
                    <Button
                      iconRight="add"
                      type="primary"
                      onClick={onCreateCourse}
                    >
                      <Trans i18nKey="learning:academy_new_course" />
                    </Button>
                  )}
                />
              )}
            />
          </DndProvider>
        </>
      )}
    </Container.Content>
  );
};

export default connector(
  pageWrapper(EEventNames.VISITED_ACADEMY_PAGE)(LearningAcademyContainer)
);

export const getTitleKey = (status?: EStatus) => {
  if (!status) return 'learning:academy_courses';
  return TITLE_KEYS[status];
};

export function getFilteredItems(
  courses: any[],
  predicates: any[],
  search: string,
  items: any[],
  params: { status?: EStatus } = {},
) {
  const coursesLookup = R.indexBy(R.prop('id'), courses);

  const networkFilter = predicates.find((p: any) => {
    return p.value.attribute === EPredicateFields.NETWORK;
  })?.value.value;
  const functionFilter = predicates.find((p: any) => {
    return p.value.attribute === EPredicateFields.FUNCTION;
  })?.value.value;

  const filteredItems = items.map((id) => coursesLookup[id]).filter((item: any) => {
    return (
      !!item &&
      (
        search ?
          searchUtil.includesInQuery(item, search, ['name', 'description']) :
          true
      ) &&
      (
        networkFilter?.length ?
          !!intersection(
            networkFilter,
            item.filters.networks.map((n: any) => n.id)
          ).length :
          true
      ) &&
      (
        functionFilter?.length ?
          !!intersection(
            functionFilter,
            item.filters.functions.map((n: any) => n.id)
          ).length :
          true
      ) &&
      (params.status === EStatus.LIVE ? item.published && !item.archived : true) &&
      (params.status === EStatus.DRAFT ? !item.published && !item.archived : true) &&
      (params.status === EStatus.ARCHIVED ? item.archived : true)
    );
  });
  return filteredItems;
}
